/**
 * "Download PDF" chooser for docx-native deal documents.
 *
 * Only shown when the document HAS extra attachments. With none, the caller
 * prints directly — instant, client-side, no round trip. This modal exists for
 * the one case the browser genuinely cannot handle: concatenating the rendered
 * document with attachment PDFs, which has to happen server-side (HexaPDF).
 */

import React, { useState } from 'react';

export interface PdfAttachment {
  id: string;
  /** Display name — falls back to the linked document's title. */
  label: string;
}

export function DownloadPdfModal({
  attachments,
  onCancel,
  onDocumentOnly,
  onMerge,
}: {
  attachments: PdfAttachment[];
  onCancel: () => void;
  /** Instant client-side print of the document by itself. */
  onDocumentOnly: () => void;
  /** Server-side render + merge of the selected attachments. */
  onMerge: (selectedIds: string[]) => void;
}) {
  // Default to everything selected — the user opened this because they have
  // attachments, so "all" is the likely intent.
  const [selected, setSelected] = useState<string[]>(() => attachments.map((a) => a.id));

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div style={BACKDROP} role="presentation" onClick={onCancel}>
      <div
        style={PANEL}
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-pdf-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="download-pdf-title" style={TITLE}>
          Download PDF
        </h2>
        <p style={BODY}>
          This document has {attachments.length} attachment
          {attachments.length === 1 ? '' : 's'}. Include{' '}
          {attachments.length === 1 ? 'it' : 'them'} in the PDF?
        </p>

        <div style={LIST}>
          {attachments.map((a) => (
            <label key={a.id} style={ROW}>
              <input
                type="checkbox"
                checked={selected.includes(a.id)}
                onChange={() => toggle(a.id)}
              />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {/* Defensive: never render a non-string label as a React child. */}
                {typeof a.label === 'string' ? a.label : String(a.label ?? 'Attachment')}
              </span>
            </label>
          ))}
        </div>

        <div style={ACTIONS}>
          <button type="button" style={GHOST_BTN} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" style={GHOST_BTN} onClick={onDocumentOnly}>
            Document only
          </button>
          <button
            type="button"
            style={{ ...PRIMARY_BTN, opacity: selected.length ? 1 : 0.5 }}
            disabled={!selected.length}
            onClick={() => onMerge(selected)}
          >
            Merge {selected.length} attachment{selected.length === 1 ? '' : 's'}
          </button>
        </div>
        {/* Set expectations: merging is a server render, not an instant print. */}
        <p style={FOOTNOTE}>
          Merging renders on the server and will download when it's ready. "Document only" is
          instant.
        </p>
      </div>
    </div>
  );
}

const BACKDROP: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};
const PANEL: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  padding: 24,
  width: 'min(480px, calc(100vw - 32px))',
  boxShadow: '0 20px 60px rgba(15,23,42,.28)',
  fontFamily: 'Arial, sans-serif',
};
const TITLE: React.CSSProperties = { margin: '0 0 8px', fontSize: 18, fontWeight: 600 };
const BODY: React.CSSProperties = { margin: '0 0 16px', fontSize: 14, color: '#3c4043' };
const LIST: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  maxHeight: 220,
  overflowY: 'auto',
  marginBottom: 20,
};
const ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  cursor: 'pointer',
};
const ACTIONS: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  flexWrap: 'wrap',
};
const GHOST_BTN: React.CSSProperties = {
  height: 36,
  padding: '0 14px',
  border: '1px solid #d6dae0',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};
const PRIMARY_BTN: React.CSSProperties = {
  height: 36,
  padding: '0 16px',
  border: 'none',
  borderRadius: 6,
  background: '#006c90',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
};
const FOOTNOTE: React.CSSProperties = {
  margin: '14px 0 0',
  fontSize: 12,
  color: '#5f6368',
};
